import { useEffect, useReducer, useRef, useState } from "react";
import Table from "src/components/Table";
import { PermissionCell } from "src/components/Table/Cells/Employees/PermissionCell";
import { HeaderSortingCell, NameCell } from "src/components/Table/Cells/shared";
import { HeaderCell } from "src/components/Table/Cells/shared/HeaderCell";
import { HeaderRow, Row } from "src/components/Table/Table.styles";
import { API_PREFIX_PATH } from "src/constants";
import { TableFilterProvider } from "src/context";
import { useApiClient, useSafeApiCall } from "src/hooks/api";
import type { QueryParamsType } from "src/hooks/api/useApiClient";
import { useDealer } from "src/hooks/useDealer";
import type { EditEmployeeFormData, Employee } from "src/types/employee";
import FilterControls from "../FilterControls/FilterControls";
import PaginationControls from "../PaginationControls";
import { LastActiveCell } from "../Table/Cells/Employees/LastActiveCell";
import { MenuCell } from "../Table/Cells/Employees/MenuCell";
import AddEmployeeModal from "./AddEmployeeModal";
import DeactivateEmployeeDialog from "./DeactivateEmployeeDialog";
import EditEmployeeDrawer from "./EditEmployeeDrawer";
import {
  postDealerPortalV1DealerIdEmployees,
  patchDealerPortalV1DealerIdEmployeesEmployeeId,
  type DtoCreateDealerEmployeeReq,
  type DtoUpdateDealerEmployeeReq,
  type DomainDealerEmployeeRole,
} from "@porchpass/api-client";
import { useApiCall } from "@porchpass/ui";
interface GetEmployeesResponseDTO {
  items: Employee[];
  total: number;
}

interface EmployeeTableProps {
  updateTotalEmployeeCount: (count: number) => void;
  allowPagination?: boolean;
  onRegisterCreateHandler?: (handler: () => void) => void;
  canManageEmployees: boolean;
}

const cleanRole = (role: string) => role.toLowerCase().replace("_", " ");

export default ({
  updateTotalEmployeeCount,
  allowPagination = true,
  onRegisterCreateHandler,
  canManageEmployees,
}: EmployeeTableProps) => {
  const { dealer_id } = useDealer();
  const [employees, setEmployees] = useState<GetEmployeesResponseDTO>({
    items: [],
    total: 0,
  });
  const [loadData, reloadData] = useReducer((x) => !x, false);
  const [activeEmployee, setActiveEmployee] = useState<Employee | undefined>();
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [employeeToDeactivate, setEmployeeToDeactivate] =
    useState<Employee | null>(null);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  // Pending navigation: direction and the offset we're navigating to
  const [pendingNavigation, setPendingNavigation] = useState<{
    direction: "first" | "last";
    forOffset: number;
  } | null>(null);
  // Track which offset the current employees data corresponds to
  const [loadedOffset, setLoadedOffset] = useState(0);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [sortField, setSortField] = useState<string | null>(null);
  const [textSearchField, setTextSearchField] = useState<string | null>(null);
  const [textSearchValue, setTextSearchValue] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<{
    start: string | null;
    end: string | null;
    period?: string;
  }>({ start: null, end: null });
  const [applicationDate, setApplicationDate] = useState<{
    start: string | null;
    end: string | null;
    period?: string;
  }>({ start: null, end: null });
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const latestFetch = useRef(0);
  const safeApiCall = useSafeApiCall();
  const { fetchFromApi } = useApiClient();

  useEffect(() => {
    const currentFetchId = latestFetch.current + 1;
    latestFetch.current = currentFetchId;
    const isCurrentFetch = true;

    const queryParams: QueryParamsType = {
      limit: pageSize,
      offset,
    };

    if (textSearchValue && textSearchField) {
      queryParams[textSearchField] = textSearchValue.trim();
    }
    if (role) {
      queryParams.role = role;
    }

    if (sortField) {
      queryParams.sort = sortField;
      queryParams.order = sortDirection;
    }

    const path = `${API_PREFIX_PATH}/${dealer_id}/employees`;
    const currentOffset = offset; // Capture offset for this fetch
    safeApiCall(
      () =>
        fetchFromApi(path, { queryParams }) as Promise<GetEmployeesResponseDTO>,
    )
      .then((data) => {
        if (isCurrentFetch && latestFetch.current === currentFetchId) {
          setEmployees(data);
          setLoadedOffset(currentOffset);
          updateTotalEmployeeCount(data.total);
        }
      })
      .catch(() => {
        if (isCurrentFetch && latestFetch.current === currentFetchId) return;
      });
  }, [
    offset,
    pageSize,
    sortField,
    sortDirection,
    textSearchField,
    textSearchValue,
    role,
    loadData,
  ]);

  useEffect(() => {
    setOffset(0);
  }, [sortField, sortDirection, textSearchField, textSearchValue, role]);

  // Handle pending navigation after page data loads
  useEffect(() => {
    if (
      pendingNavigation &&
      pendingNavigation.forOffset === loadedOffset &&
      employees.items.length > 0
    ) {
      if (pendingNavigation.direction === "first") {
        setActiveEmployee(employees.items[0]);
      } else if (pendingNavigation.direction === "last") {
        setActiveEmployee(employees.items[employees.items.length - 1]);
      }
      setPendingNavigation(null);
    }
  }, [employees.items, pendingNavigation, loadedOffset]);

  const onSort = (field: string | null) => () => {
    if (sortField && sortField === field) {
      setSortDirection(sortDirection === "desc" ? "asc" : "desc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Add Employee API call using useApiCall hook
  const addEmployee = useApiCall(
    (request: DtoCreateDealerEmployeeReq) => {
      if (!dealer_id) return Promise.reject(new Error("No dealer ID"));
      return postDealerPortalV1DealerIdEmployees(dealer_id, request);
    },
    {
      toast: {
        success: "Employee added successfully",
        useServerErrorMessage: true,
      },
      onSuccess: () => {
        setAddModalOpen(false);
        reloadData();
      },
    },
  );

  // Edit Employee API call using useApiCall hook
  const editEmployee = useApiCall(
    (request: DtoUpdateDealerEmployeeReq) => {
      if (!dealer_id || !activeEmployee?.id) {
        return Promise.reject(new Error("No dealer or employee ID"));
      }
      return patchDealerPortalV1DealerIdEmployeesEmployeeId(
        dealer_id,
        activeEmployee.id,
        request,
      );
    },
    {
      toast: {
        success: "Employee updated successfully",
        useServerErrorMessage: true,
      },
      onSuccess: () => {
        setEditDrawerOpen(false);
        setActiveEmployee(undefined);
        reloadData();
      },
    },
  );

  // Handler called by AddEmployeeModal
  const addEmployeeSaveHandler = async (
    employeeData: EditEmployeeFormData,
  ): Promise<void> => {
    const request: DtoCreateDealerEmployeeReq = {
      first_name: employeeData.first_name,
      last_name: employeeData.last_name,
      email: employeeData.email,
      role: employeeData.role as DomainDealerEmployeeRole,
      phone_number: employeeData.phone_number,
      title: employeeData.title,
    };

    await addEmployee.execute(request);
  };

  // Handler called by EditEmployeeDrawer
  const editEmployeeSaveHandler = async (
    employeeData: EditEmployeeFormData,
  ): Promise<void> => {
    const request: DtoUpdateDealerEmployeeReq = {
      first_name: employeeData.first_name,
      last_name: employeeData.last_name,
      email: employeeData.email,
      role: employeeData.role as DomainDealerEmployeeRole,
      phone_number: employeeData.phone_number,
      title: employeeData.title,
    };

    await editEmployee.execute(request);
  };

  const editEmployeeDeactivateHandler = async (): Promise<void> => {
    if (!activeEmployee?.id) return;

    const path = `${API_PREFIX_PATH}/${dealer_id}/employees/${activeEmployee.id}`;

    await safeApiCall(
      () => fetchFromApi(path, { method: "DELETE" }) as Promise<void>,
      {
        toastSuccessMsg: "Employee deactivated successfully",
        toastErrorMsg: "Failed to deactivate employee",
      },
    );

    setEditDrawerOpen(false);
    setActiveEmployee(undefined);
    reloadData(); // Refresh the employee list
  };

  const handleDeactivateFromMenu = async (): Promise<void> => {
    if (!employeeToDeactivate?.id) return;

    const path = `${API_PREFIX_PATH}/${dealer_id}/employees/${employeeToDeactivate.id}`;

    await safeApiCall(
      () => fetchFromApi(path, { method: "DELETE" }) as Promise<void>,
      {
        toastSuccessMsg: "Employee deactivated successfully",
        toastErrorMsg: "Failed to deactivate employee",
      },
    );

    setDeactivateDialogOpen(false);
    setEmployeeToDeactivate(null);
    reloadData(); // Refresh the employee list
  };

  const handleOpenDeactivateDialog = (employee: Employee) => {
    setEmployeeToDeactivate(employee);
    setDeactivateDialogOpen(true);
  };

  const openEditDrawer = (employee: Employee) => {
    setActiveEmployee(employee);
    setEditDrawerOpen(true);
  };

  const handleEditDrawerOpenChange = (open: boolean) => {
    setEditDrawerOpen(open);
    if (!open) {
      setActiveEmployee(undefined);
    }
  };

  // Navigation handlers for cross-page navigation with wrapping
  const handleNavigatePrevious = () => {
    if (!activeEmployee || employees.items.length === 0) return;

    const currentIndex = employees.items.findIndex(
      (e) => e.id === activeEmployee.id,
    );
    const totalPages = Math.ceil(employees.total / pageSize);
    const currentPage = Math.floor(offset / pageSize);

    if (currentIndex > 0) {
      // Navigate within current page
      setActiveEmployee(employees.items[currentIndex - 1]);
    } else {
      // Need to go to previous page (or wrap to last page)
      if (currentPage > 0) {
        // Go to previous page, select last employee
        const newOffset = offset - pageSize;
        setOffset(newOffset);
        setPendingNavigation({ direction: "last", forOffset: newOffset });
      } else {
        // On first page, wrap to last page
        const lastPageOffset = (totalPages - 1) * pageSize;
        setOffset(lastPageOffset);
        setPendingNavigation({ direction: "last", forOffset: lastPageOffset });
      }
    }
  };

  const handleNavigateNext = () => {
    if (!activeEmployee || employees.items.length === 0) return;

    const currentIndex = employees.items.findIndex(
      (e) => e.id === activeEmployee.id,
    );
    const totalPages = Math.ceil(employees.total / pageSize);
    const currentPage = Math.floor(offset / pageSize);

    if (currentIndex < employees.items.length - 1) {
      // Navigate within current page
      setActiveEmployee(employees.items[currentIndex + 1]);
    } else {
      // Need to go to next page (or wrap to first page)
      if (currentPage < totalPages - 1) {
        // Go to next page, select first employee
        const newOffset = offset + pageSize;
        setOffset(newOffset);
        setPendingNavigation({ direction: "first", forOffset: newOffset });
      } else {
        // On last page, wrap to first page
        setOffset(0);
        setPendingNavigation({ direction: "first", forOffset: 0 });
      }
    }
  };

  const Header = () => (
    <HeaderRow>
      <HeaderSortingCell
        title="Name"
        sortDirection={sortDirection}
        onSort={onSort("name")}
      />
      <HeaderSortingCell
        title="Permissions"
        sortDirection={sortDirection}
        onSort={onSort("role")}
      />
      <HeaderCell title="Last Active" />
      <HeaderCell />
    </HeaderRow>
  );

  const createNewEmployeeClickHandler = () => {
    setAddModalOpen(true);
  };

  // Register the create handler with the parent component
  useEffect(() => {
    if (onRegisterCreateHandler && canManageEmployees) {
      onRegisterCreateHandler(createNewEmployeeClickHandler);
    }
  }, [onRegisterCreateHandler, canManageEmployees]);

  if (employees === undefined) {
    return null;
  }

  return (
    <>
      <TableFilterProvider
        value={{
          table: "employees",
          textSearchField,
          setTextSearchField,
          textSearchValue,
          setTextSearchValue,
          role,
          setRole,
          lastUpdated,
          setLastUpdated,
          applicationDate,
          setApplicationDate,
          selectedStatuses,
          setSelectedStatuses,
        }}
      >
        <FilterControls />
      </TableFilterProvider>
      <Table
        Header={Header}
        rowBuilder={(obj) => {
          const {
            id,
            first_name,
            last_name,
            role,
            email,
            last_activity,
            phone_number,
            title,
          } = obj as unknown as Employee;
          return (
            <Row removeHover={true} key={String(id)}>
              <NameCell
                searchSelection={textSearchField}
                firstName={first_name}
                lastName={last_name}
                email={email}
              />
              <PermissionCell>{cleanRole(role)}</PermissionCell>
              <LastActiveCell date={last_activity} />
              <MenuCell
                canManageEmployees={canManageEmployees}
                setActiveEmployee={() =>
                  openEditDrawer({
                    id,
                    first_name,
                    last_name,
                    role,
                    email,
                    last_activity,
                    phone_number,
                    title,
                  })
                }
                onDeactivate={() =>
                  handleOpenDeactivateDialog({
                    id,
                    first_name,
                    last_name,
                    role,
                    email,
                    last_activity,
                    phone_number,
                    title,
                  })
                }
              />
            </Row>
          );
        }}
        data={employees.items as unknown as Record<string, unknown>[]}
      />
      {allowPagination && (
        <PaginationControls
          offset={offset}
          setOffset={setOffset}
          pageSize={pageSize}
          setPageSize={setPageSize}
          totalCount={employees.total}
        />
      )}

      <AddEmployeeModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        onSave={addEmployeeSaveHandler}
        isSaving={addEmployee.isLoading}
      />

      <EditEmployeeDrawer
        open={editDrawerOpen}
        onOpenChange={handleEditDrawerOpenChange}
        employee={activeEmployee || null}
        onSave={editEmployeeSaveHandler}
        isSaving={editEmployee.isLoading}
        onDeactivate={editEmployeeDeactivateHandler}
        onNavigatePrevious={handleNavigatePrevious}
        onNavigateNext={handleNavigateNext}
      />

      {employeeToDeactivate && (
        <DeactivateEmployeeDialog
          open={deactivateDialogOpen}
          onOpenChange={setDeactivateDialogOpen}
          employeeName={`${employeeToDeactivate.first_name} ${employeeToDeactivate.last_name}`}
          onConfirm={handleDeactivateFromMenu}
        />
      )}
    </>
  );
};
